import { BaseService } from "../../core/baseService";
import {
  CopyWeekScheduleDto,
  CreateLeaveRequestDto,
  CreateLeaveTypeDto,
  CreateShiftTemplateDto,
  CreateWorkShiftDto,
  CreateWorkerDto,
  CreateWorkerGroupDto,
  GetLeaveRequestsParams,
  GetSchedulePublishesParams,
  GetShiftsParams,
  GetWorkersParams,
  LeaveRequestDecisionDto,
  PublishWeeklyScheduleDto,
  ShiftTemplateDto,
  UpdateLeaveBalanceDto,
  UpdateLeaveRequestDto,
  UpdateLeaveTypeDto,
  UpdateShiftTemplateDto,
  UpdateWorkShiftDto,
  UpdateWorkerDto,
  UpdateWorkerGroupDto,
  WorkforceServiceInterface,
  WorkerDto,
  WorkerGroupDto,
  WorkShiftDto,
  LeaveTypeDto,
  LeaveRequestDto,
  LeaveBalanceDto,
  WeeklySchedulePublishDto,
  ShareWorkerScheduleDto,
} from "../types/workforce.types";

export class WorkforceService extends BaseService implements WorkforceServiceInterface {
  async getWorkers(params: GetWorkersParams = {}) {
    const queryParams = this.buildQueryParams({
      includeInactive: params.includeInactive,
      groupId: params.groupId,
      search: params.search,
    });

    return this.handleRequest<WorkerDto[]>(
      this.client.get("/api/admin/workforce/workers", { params: queryParams }),
    );
  }

  async getWorkerById(workerId: string) {
    return this.handleRequest<WorkerDto>(
      this.client.get(`/api/admin/workforce/workers/${workerId}`),
    );
  }

  async createWorker(dto: CreateWorkerDto) {
    return this.handleRequest<WorkerDto>(
      this.client.post("/api/admin/workforce/workers", dto),
    );
  }

  async updateWorker(workerId: string, dto: UpdateWorkerDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/workforce/workers/${workerId}`, dto),
    );
  }

  async deleteWorker(workerId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/workforce/workers/${workerId}`),
    );
  }

  async generateShareLink(workerId: string) {
    return this.handleRequest<ShareWorkerScheduleDto>(
      this.client.post(`/api/admin/workforce/workers/${workerId}/share-link`, {}),
    );
  }

  async revokeShareLink(workerId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/workforce/workers/${workerId}/share-link`),
    );
  }

  async getGroups(includeInactive = false) {
    const queryParams = this.buildQueryParams({ includeInactive });
    return this.handleRequest<WorkerGroupDto[]>(
      this.client.get("/api/admin/workforce/groups", { params: queryParams }),
    );
  }

  async createGroup(dto: CreateWorkerGroupDto) {
    return this.handleRequest<WorkerGroupDto>(
      this.client.post("/api/admin/workforce/groups", dto),
    );
  }

  async updateGroup(groupId: string, dto: UpdateWorkerGroupDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/workforce/groups/${groupId}`, dto),
    );
  }

  async deleteGroup(groupId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/workforce/groups/${groupId}`),
    );
  }

  async getShiftTemplates(includeInactive = false) {
    const queryParams = this.buildQueryParams({ includeInactive });
    return this.handleRequest<ShiftTemplateDto[]>(
      this.client.get("/api/admin/workforce/shift-templates", { params: queryParams }),
    );
  }

  async createShiftTemplate(dto: CreateShiftTemplateDto) {
    return this.handleRequest<ShiftTemplateDto>(
      this.client.post("/api/admin/workforce/shift-templates", dto),
    );
  }

  async updateShiftTemplate(templateId: string, dto: UpdateShiftTemplateDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/workforce/shift-templates/${templateId}`, dto),
    );
  }

  async deleteShiftTemplate(templateId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/workforce/shift-templates/${templateId}`),
    );
  }

  async getShifts(params: GetShiftsParams = {}) {
    const queryParams = this.buildQueryParams({
      fromUtc: params.fromUtc,
      toUtc: params.toUtc,
      workerId: params.workerId,
      groupId: params.groupId,
    });

    return this.handleRequest<WorkShiftDto[]>(
      this.client.get("/api/admin/workforce/shifts", { params: queryParams }),
    );
  }

  async getShiftById(shiftId: string) {
    return this.handleRequest<WorkShiftDto>(
      this.client.get(`/api/admin/workforce/shifts/${shiftId}`),
    );
  }

  async createShift(dto: CreateWorkShiftDto) {
    return this.handleRequest<WorkShiftDto>(
      this.client.post("/api/admin/workforce/shifts", dto),
    );
  }

  async updateShift(shiftId: string, dto: UpdateWorkShiftDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/workforce/shifts/${shiftId}`, dto),
    );
  }

  async deleteShift(shiftId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/workforce/shifts/${shiftId}`),
    );
  }

  async copyWeek(dto: CopyWeekScheduleDto) {
    return this.handleRequest<void>(
      this.client.post("/api/admin/workforce/shifts/copy-week", dto),
    );
  }

  async getLeaveTypes(includeInactive = false) {
    const queryParams = this.buildQueryParams({ includeInactive });
    return this.handleRequest<LeaveTypeDto[]>(
      this.client.get("/api/admin/workforce/leave-types", { params: queryParams }),
    );
  }

  async createLeaveType(dto: CreateLeaveTypeDto) {
    return this.handleRequest<LeaveTypeDto>(
      this.client.post("/api/admin/workforce/leave-types", dto),
    );
  }

  async updateLeaveType(leaveTypeId: string, dto: UpdateLeaveTypeDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/workforce/leave-types/${leaveTypeId}`, dto),
    );
  }

  async deleteLeaveType(leaveTypeId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/workforce/leave-types/${leaveTypeId}`),
    );
  }

  async getLeaveRequests(params: GetLeaveRequestsParams = {}) {
    const queryParams = this.buildQueryParams({
      status: params.status,
      workerId: params.workerId,
      fromDate: params.fromDate,
      toDate: params.toDate,
    });

    return this.handleRequest<LeaveRequestDto[]>(
      this.client.get("/api/admin/workforce/leave-requests", { params: queryParams }),
    );
  }

  async createLeaveRequest(dto: CreateLeaveRequestDto) {
    return this.handleRequest<LeaveRequestDto>(
      this.client.post("/api/admin/workforce/leave-requests", dto),
    );
  }

  async updateLeaveRequest(leaveRequestId: string, dto: UpdateLeaveRequestDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/workforce/leave-requests/${leaveRequestId}`, dto),
    );
  }

  async approveLeaveRequest(leaveRequestId: string, dto: LeaveRequestDecisionDto = {}) {
    return this.handleRequest<void>(
      this.client.post(`/api/admin/workforce/leave-requests/${leaveRequestId}/approve`, dto),
    );
  }

  async rejectLeaveRequest(leaveRequestId: string, dto: LeaveRequestDecisionDto = {}) {
    return this.handleRequest<void>(
      this.client.post(`/api/admin/workforce/leave-requests/${leaveRequestId}/reject`, dto),
    );
  }

  async cancelLeaveRequest(leaveRequestId: string, dto: LeaveRequestDecisionDto = {}) {
    return this.handleRequest<void>(
      this.client.post(`/api/admin/workforce/leave-requests/${leaveRequestId}/cancel`, dto),
    );
  }

  async getLeaveBalances(workerId: string, year?: number) {
    const queryParams = this.buildQueryParams({ year });
    return this.handleRequest<LeaveBalanceDto[]>(
      this.client.get(`/api/admin/workforce/workers/${workerId}/leave-balances`, { params: queryParams }),
    );
  }

  async upsertLeaveBalance(workerId: string, dto: UpdateLeaveBalanceDto) {
    return this.handleRequest<LeaveBalanceDto>(
      this.client.put(`/api/admin/workforce/workers/${workerId}/leave-balances`, dto),
    );
  }

  async getSchedulePublishes(params: GetSchedulePublishesParams = {}) {
    const queryParams = this.buildQueryParams({
      fromDate: params.fromDate,
      toDate: params.toDate,
    });

    return this.handleRequest<WeeklySchedulePublishDto[]>(
      this.client.get("/api/admin/workforce/schedule-publishes", { params: queryParams }),
    );
  }

  async publishSchedule(dto: PublishWeeklyScheduleDto) {
    return this.handleRequest<WeeklySchedulePublishDto>(
      this.client.post("/api/admin/workforce/schedule-publishes", dto),
    );
  }

  async syncStaffWorkers() {
    return this.handleRequest<{ message: string }>(
      this.client.post("/api/admin/workforce/sync-staff-workers", {}),
    );
  }
}
